<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    header="Page Settings"
    :style="{ width: '32rem' }"
    :dismissable-mask="true"
  >
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <!-- Page Title -->
      <div class="space-y-2">
        <label for="title" class="block text-sm font-medium text-gray-700">
          Page Title
        </label>
        <InputText
          id="title"
          v-model="formData.title"
          class="w-full"
          :class="{ 'p-invalid': errors.title }"
        />
        <small v-if="errors.title" class="text-red-500">{{ errors.title }}</small>
      </div>

      <!-- Page Slug -->
      <div class="space-y-2">
        <label for="slug" class="block text-sm font-medium text-gray-700">
          URL Slug
        </label>
        <InputText
          id="slug"
          v-model="formData.slug"
          class="w-full"
          :class="{ 'p-invalid': errors.slug }"
        />
        <small class="text-gray-500">URL path: /project/{{ formData.slug }}</small>
        <small v-if="errors.slug" class="text-red-500">{{ errors.slug }}</small>
      </div>

      <!-- Layout Type -->
      <div class="space-y-2">
        <label for="layoutType" class="block text-sm font-medium text-gray-700">
          Layout Type
        </label>
        <Dropdown
          id="layoutType"
          v-model="formData.layoutType"
          :options="layoutOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          :class="{ 'p-invalid': errors.layoutType }"
        />
        <small class="text-gray-500">{{ getLayoutDescription(formData.layoutType) }}</small>
        <small v-if="errors.layoutType" class="text-red-500">{{ errors.layoutType }}</small>
      </div>

      <!-- Page Status Display -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700">
          Current Status
        </label>
        <div class="flex items-center space-x-2">
          <i :class="getStatusIcon(page.status ?? 'draft')" class="text-sm"></i>
          <span class="text-sm">{{ getStatusLabel(page.status ?? 'draft') }}</span>
        </div>
        <small class="text-gray-500">
          Use Publish button in header to change status
        </small>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-between w-full">
        <Button
          label="Delete Page"
          severity="danger"
          outlined
          icon="pi pi-trash"
          :loading="isDeleting"
          @click="handleDelete"
        />
        <div class="flex space-x-2">
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            @click="dialogVisible = false"
          />
          <Button
            label="Save Changes"
            :loading="isSaving"
            @click="handleSubmit"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import { useDeletePageMutation } from '@/composables/usePages'

interface Page {
  id: number
  title: string
  slug: string
  layoutType: string
  status?: 'draft' | 'published' | 'archived'
}

interface Props {
  visible: boolean
  page: Page
}

interface FormData {
  title: string
  slug: string
  layoutType: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [visible: boolean]
  'update': [pageData: FormData & { id: number }]
}>()

const confirm = useConfirm()
const toast = useToast()
const deleteMutation = useDeletePageMutation()

// Form state
const formData = ref<FormData>({
  title: '',
  slug: '',
  layoutType: 'default'
})

const errors = ref<Partial<FormData>>({})
const isSaving = ref(false)
const isDeleting = ref(false)

// Dialog visibility binding
const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

// Layout options
const layoutOptions = [
  {
    label: 'Default',
    value: 'default',
    description: 'Standard page with header and footer'
  },
  {
    label: 'Landing Page',
    value: 'landing',
    description: 'Full-width layout for landing pages'
  },
  {
    label: 'Minimal',
    value: 'minimal',
    description: 'Clean layout without header/footer'
  },
  {
    label: 'Special',
    value: 'special',
    description: 'Custom layout for special pages'
  }
]

// Layout description helper
const getLayoutDescription = (layoutType: string) => {
  const option = layoutOptions.find(opt => opt.value === layoutType)
  return option?.description || ''
}

// Status helpers
const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    'published': 'pi-check-circle text-green-500',
    'draft': 'pi-clock text-orange-500',
    'archived': 'pi-archive text-gray-500'
  }
  return icons[status] || 'pi-circle text-gray-400'
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    'published': 'Published',
    'draft': 'Draft',
    'archived': 'Archived'
  }
  return labels[status] || 'Unknown'
}

// Form validation
const validateForm = (): boolean => {
  errors.value = {}

  if (!formData.value.title.trim()) {
    errors.value.title = 'Page title is required'
  }

  if (!formData.value.slug.trim()) {
    errors.value.slug = 'URL slug is required'
  } else if (!/^[a-z0-9-]+$/.test(formData.value.slug)) {
    errors.value.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'
  }

  if (!formData.value.layoutType) {
    errors.value.layoutType = 'Layout type is required'
  }

  return Object.keys(errors.value).length === 0
}

// Auto-generate slug from title
watch(() => formData.value.title, (newTitle) => {
  if (!formData.value.slug || formData.value.slug === '') {
    // Auto-generate slug from title
    const slug = newTitle
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim()
    formData.value.slug = slug
  }
})

// Initialize form data when page changes
watch(() => props.page, (newPage) => {
  if (newPage) {
    formData.value = {
      title: newPage.title,
      slug: newPage.slug,
      layoutType: newPage.layoutType
    }
  }
}, { immediate: true })

// Form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isSaving.value = true

  try {
    // Emit update event with form data
    emit('update', {
      id: props.page.id,
      ...formData.value
    })
  } catch (error) {
    console.error('Failed to update page:', error)
  } finally {
    isSaving.value = false
  }
}

// Delete page with confirmation
const handleDelete = () => {
  confirm.require({
    message: `Are you sure you want to delete "${props.page.title}"? This action cannot be undone.`,
    header: 'Delete Page',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async () => {
      isDeleting.value = true
      try {
        await deleteMutation.mutateAsync(props.page.id)
        toast.add({
          severity: 'success',
          summary: 'Page Deleted',
          detail: `"${props.page.title}" has been deleted`,
          life: 3000
        })
        dialogVisible.value = false
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete page',
          life: 5000
        })
      } finally {
        isDeleting.value = false
      }
    }
  })
}
</script>

<style scoped>
/* Additional styling for form elements */
.p-invalid {
  border-color: #ef4444 !important;
}
</style>